import React from "react";
import { Link } from "react-router-dom";
import {
  AcademicCapIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  GlobeAltIcon,
  UserGroupIcon,
  ClipboardDocumentCheckIcon,
  CheckCircleIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";

const Services: React.FC = () => {
  const mainServices = [
    {
      icon: AcademicCapIcon,
      title: "University Admission Consulting",
      description:
        "Expert guidance for Korean university applications with personalized counseling",
      features: [
        "University selection based on your academic profile",
        "Application strategy and timeline planning",
        "Academic requirement assessment",
        "Program-specific guidance for STEM, Business, Arts",
        "Entrance exam preparation support",
      ],
      popular: true,
    },
    {
      icon: DocumentTextIcon,
      title: "Document Preparation & Translation",
      description:
        "Professional document services for seamless university applications",
      features: [
        "Academic transcript translation and verification",
        "Statement of Purpose writing assistance",
        "Letter of Recommendation guidance",
        "Portfolio preparation for creative programs",
        "Official document authentication and apostille",
      ],
    },
    {
      icon: CurrencyDollarIcon,
      title: "Scholarship & Financial Aid",
      description:
        "Maximize your funding opportunities with our scholarship expertise",
      features: [
        "Government scholarship application (KGSP, GKS)",
        "University-specific scholarship guidance",
        "Merit-based scholarship opportunities",
        "Financial planning and budgeting assistance",
        "Scholarship essay writing support",
      ],
    },
    {
      icon: GlobeAltIcon,
      title: "Visa & Immigration Support",
      description: "Complete visa assistance for smooth entry to South Korea",
      features: [
        "Student visa (D-2) application guidance",
        "Visa document checklist and preparation",
        "Embassy appointment scheduling",
        "Visa interview preparation",
        "Immigration compliance guidance",
      ],
    },
    {
      icon: UserGroupIcon,
      title: "Pre-Departure & Arrival Support",
      description:
        "Comprehensive support for your transition to Korean university life",
      features: [
        "Airport pickup and accommodation assistance",
        "University registration and enrollment support",
        "Banking and mobile phone setup guidance",
        "Cultural orientation and Korean language basics",
        "Local SIM card and transportation guidance",
      ],
    },
    {
      icon: ClipboardDocumentCheckIcon,
      title: "Academic & Career Counseling",
      description: "Ongoing support throughout your academic journey in Korea",
      features: [
        "Course selection and academic planning",
        "Internship and job search assistance",
        "Graduate school application support",
        "Industry networking opportunities",
        "Alumni mentorship programs",
      ],
    },
  ];

  const processSteps = [
    {
      step: "01",
      title: "Initial Consultation",
      description:
        "Free consultation to assess your academic goals and university preferences",
    },
    {
      step: "02",
      title: "University Selection",
      description:
        "Personalized university recommendations based on your profile and career goals",
    },
    {
      step: "03",
      title: "Application Preparation",
      description:
        "Complete application package preparation with document support",
    },
    {
      step: "04",
      title: "Submission & Follow-up",
      description:
        "Application submission and regular follow-up with universities",
    },
    {
      step: "05",
      title: "Visa & Departure",
      description: "Visa processing assistance and pre-departure orientation",
    },
    {
      step: "06",
      title: "Arrival Support",
      description: "On-ground support for smooth transition to university life",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Our Services
            </h1>
            <p className="text-xl md:text-2xl text-cyan-100 mb-8 leading-relaxed">
              Comprehensive study abroad consulting services for Korean
              university admissions. From application to arrival, we support
              your entire journey.
            </p>
            <Link
              to="/signin"
              className="bg-orange-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-orange-600 transition-colors inline-flex items-center"
            >
              Start your Application
              <ArrowRightIcon className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Main Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Complete Study Abroad Solutions
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Our comprehensive services cover every aspect of your Korean
                university journey
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {mainServices.map((service, index) => (
                <div
                  key={index}
                  className={`bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow p-6 border ${
                    service.popular
                      ? "border-orange-500 relative"
                      : "border-gray-200"
                  }`}
                >
                  {service.popular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="bg-orange-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                        Most Popular
                      </span>
                    </div>
                  )}
                  <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center mb-4">
                    <service.icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <li
                        key={featureIndex}
                        className="flex items-start text-sm text-gray-600"
                      >
                        <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Our Proven Process
              </h2>
              <p className="text-xl text-gray-600">
                Step-by-step guidance from consultation to university enrollment
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {processSteps.map((step, index) => (
                <div key={index} className="relative">
                  <div className="bg-white rounded-xl p-6 shadow-lg">
                    <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full flex items-center justify-center mb-4 text-white font-bold">
                      {step.step}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {step.title}
                    </h3>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                  {index < processSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                      <ArrowRightIcon className="h-6 w-6 text-cyan-500" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
